import type { ReactNode } from 'react';

import { cn } from '@/shared/utils/cn';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../primitives/Dialog';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl';

const MODAL_MAX_WIDTH: Record<ModalSize, string> = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-3xl',
};

export interface ModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: ReactNode;
  description?: ReactNode;
  /** Applied to {@link DialogDescription} when `description` is set. */
  descriptionClassName?: string;
  children: ReactNode;
  footer?: ReactNode;
  /**
   * Sets `max-w-*` on the dialog shell. Omit to keep the primitive default (`max-w-[720px]`).
   */
  size?: ModalSize;
  contentClassName?: string;
  bodyClassName?: string;
  footerClassName?: string;
  showClose?: boolean;
}

/**
 * Standard footer row: stacked on narrow viewports (primary action first), horizontal on `sm+`.
 * Place **secondary** actions first in `children`, then **primary**, so the primary button sits on the right on desktop.
 */
export function ModalActions({ children }: { children: ReactNode }) {
  return (
    <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-3 [&>button]:w-full [&>button]:sm:w-auto">
      {children}
    </div>
  );
}

export function Modal({
  open,
  onOpenChange,
  title,
  description,
  descriptionClassName,
  children,
  footer,
  size,
  contentClassName,
  bodyClassName,
  footerClassName,
  showClose = false,
}: ModalProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showClose={showClose}
        className={cn(
          'flex max-h-[min(90vh,calc(100vh-2rem))] flex-col overflow-hidden',
          size != null ? MODAL_MAX_WIDTH[size] : 'max-w-[720px]',
          contentClassName,
        )}
      >
        {(title ?? description) ? (
          <DialogHeader
            className={cn(
              'shrink-0 space-y-1 border-b border-border px-6 pb-3 pt-4 text-left',
              showClose && 'pr-12 sm:pr-14',
            )}
          >
            {title ? (
              <DialogTitle className="text-lg font-semibold tracking-tight">{title}</DialogTitle>
            ) : null}
            {description ? (
              <DialogDescription className={cn('text-sm text-fg-muted', descriptionClassName)}>
                {description}
              </DialogDescription>
            ) : null}
          </DialogHeader>
        ) : null}
        <div
          className={cn(
            'min-h-0 flex-1 overflow-y-auto px-6 py-4',
            (title ?? description) ? '' : 'pt-4',
            bodyClassName,
          )}
        >
          {children}
        </div>
        {footer ? (
          <DialogFooter
            className={cn(
              'shrink-0 flex-wrap items-center justify-end gap-2 border-t border-border bg-surface-muted/50 px-6 py-3 sm:gap-3',
              footerClassName,
            )}
          >
            {footer}
          </DialogFooter>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
